/**
 * Pricing page — calculator-led, single-CTA layout (PR 6h).
 *
 * Replaces the four-card grid that made StepFlow's plans feel like
 * undifferentiated SaaS-template clones. The plans really are just
 * budget-tiered (no feature differences across Starter/Pro/Ultra), so
 * a "compare features" grid was always going to feel thin. This page
 * leads with the question buyers actually have — "will this plan fit
 * my workload?" — and answers it using the same rate sheet that bills
 * them. The dropdown is the SKU picker; the calculator is the
 * confidence builder. Both feed off ``GENERATED_PLAN_CATALOG`` and
 * ``MODEL_ROWS`` (build-time codegen from Python — never out of sync).
 *
 * Layout:
 *   1. Hero strip (status / billing-required nudge / dismiss).
 *   2. Calculator — slider for turns/month + tier toggle. Drives the
 *      dropdown's selection (and stays honest if user manually picks
 *      a different plan).
 *   3. Plan dropdown (PortalMenu, per project rule "Always use
 *      PortalMenu for any menu/popover/dropdown"). Shows the chosen
 *      plan's price + headroom in turns at the chosen tier.
 *   4. Single CTA — Subscribe / Contact sales / Continue with PayG.
 *   5. Tiny footer about catalog source-of-truth (kept from old page).
 *
 * Notes on PayG honesty: when the calculator's estimate is below
 * Starter, we surface "PayG would be cheaper at this scale" rather
 * than nudging the user toward a $20/mo plan they wouldn't fully use.
 * Master-plan §11 success metric ("no surprise bills") cuts both
 * ways — surprise to the customer AND surprise to us.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Loader2,
  X,
  Zap,
  Sparkles,
  Building2,
} from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';
import {
  fetchBillingConfig,
  createSubscriptionCheckout,
  fetchStripeBillingStatus,
  openBillingPortal,
  isBillingUiEnabled,
} from '@/services/stripeBilling';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';
import {
  GENERATED_PLAN_CATALOG,
  UNLIMITED_BUDGET,
} from '@stepflow/ui-shared/services/generatedPricing';
import {
  estimateMonthlyPoints,
  recommendPlanFor,
  turnsCoveredAt,
} from '@stepflow/ui-shared/utils/planRecommend';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { motion } from 'framer-motion';

// Shared entrance choreography — a gentle fade + rise, staggered by
// section so the page assembles top-to-bottom rather than popping in.
const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};
const ease = [0.22, 1, 0.36, 1]; // expo-out: snappy start, soft landing

const TIERS = [
  { key: 'fast',   label: 'Fast',   sub: 'Haiku',  hint: 'Routing, classification' },
  { key: 'think',  label: 'Think',  sub: 'Sonnet', hint: 'Most agent work' },
  { key: 'reason', label: 'Reason', sub: 'Opus',   hint: 'Complex reasoning' },
];

// Slider scale tuned to the actual rate sheet (Sonnet 5 — cheaper than the old 4.5):
//   * 1 Sonnet 5 turn ≈ 8 pts (1.5k input × 0.002 + 0.5k output × 0.010)
//   * Starter (25k pts)  → up to ~3,100 Sonnet turns/month
//   * Pro     (90k pts)  → up to ~11,250 Sonnet turns/month
//   * Ultra   (180k pts) → up to ~22,500 Sonnet turns/month
// Slider goes 50 → 24,000 so the user can actually reach Ultra; default
// 4,000 lands on Pro (~32k pts) for the recommendation badge effect.
const SLIDER_MIN = 50;
const SLIDER_MAX = 24_000;
const SLIDER_STEP = 50;
const DEFAULT_TURNS = 4_000;
const DEFAULT_TIER = 'think';

function formatNumber(n) {
  if (typeof n !== 'number' || !isFinite(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(Math.round(n));
}

function formatPriceCents(cents) {
  if (cents == null) return null;
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

function planIcon(planKey) {
  if (planKey === 'payg') return Zap;
  if (planKey === 'enterprise') return Building2;
  return Sparkles;
}

function planRowSummary(plan) {
  if (plan.contact_only) return 'Custom · contact us';
  const price = formatPriceCents(plan.price_cents) || '';
  if (plan.points_per_period === UNLIMITED_BUDGET) {
    return `${price} / 1k pts · metered`;
  }
  const interval = plan.interval || 'mo';
  return `${price} / ${interval} · ${formatNumber(plan.points_per_period)} pts`;
}

export default function PricingPage() {
  // Billing UI master switch. On a single-account/no-Stripe deploy
  // (``VITE_BILLING_UI_ENABLED=false``) the pricing route is a dead end —
  // redirect home rather than render a page whose CTAs would 503.
  if (!isBillingUiEnabled()) {
    return <Navigate to="/" replace />;
  }
  return <PricingPageInner />;
}

function PricingPageInner() {
  const { user, refreshBillingStatus, workspaceRole } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [config, setConfig] = useState({ publishableKey: '', plans: [] });
  const [status, setStatus] = useState(null);

  // Calculator state — owned by the page, not the catalog.
  const [turns, setTurns] = useState(DEFAULT_TURNS);
  const [tier, setTier] = useState(DEFAULT_TIER);

  // Dropdown — selected plan key. Defaults to whatever the calculator
  // recommends at mount (Pro, given DEFAULT_TURNS + DEFAULT_TIER).
  // ``manualOverride`` flips true the moment the user picks something
  // different from the dropdown — after that the calculator's
  // recommendation no longer overrides their choice; it just renders
  // a one-line truth statement against whatever they picked.
  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownAnchorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfg, st] = await Promise.all([
          fetchBillingConfig().catch(() => ({ publishableKey: '', plans: [] })),
          fetchStripeBillingStatus().catch(() => null),
        ]);
        if (!cancelled) {
          setConfig(cfg);
          setStatus(st);
        }
      } catch (e) {
        if (!cancelled) showToast(e.message || 'Could not load billing config', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  useEffect(() => {
    const sub = searchParams.get('stripe_sub');
    if (sub === '1') {
      (async () => {
        const ok = await refreshBillingStatus();
        showToast(
          ok ? 'Subscription active — welcome aboard.' : 'Payment received — syncing your account…',
          ok ? 'success' : 'info',
        );
        const next = new URLSearchParams(searchParams);
        next.delete('stripe_sub');
        setSearchParams(next, { replace: true });
        if (ok) {
          navigate(
            canAccessNetworkWorkbench(workspaceRole) ? '/network/new' : '/agentic',
            { replace: true },
          );
        }
      })();
    } else if (sub === 'cancelled') {
      showToast('Checkout cancelled.', 'info');
      const next = new URLSearchParams(searchParams);
      next.delete('stripe_sub');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, showToast, refreshBillingStatus, navigate, workspaceRole]);

  // Calculator math — pure derivation from {turns, tier}.
  const recommendation = useMemo(
    () => recommendPlanFor({ turns, tier }),
    [turns, tier],
  );
  const estimatedPoints = recommendation.estimatedPoints;

  // Sync dropdown to calculator's recommendation, until user manually
  // overrides. Once they pick something else, we keep their choice and
  // surface the truth against it ("your estimate exceeds Starter's
  // budget") rather than silently flipping back.
  useEffect(() => {
    if (!manualOverride) {
      setSelectedPlanKey(recommendation.plan?.key || null);
    }
  }, [recommendation.plan?.key, manualOverride]);

  const selectedPlan = useMemo(
    () =>
      GENERATED_PLAN_CATALOG.find(
        (p) => p.key === (selectedPlanKey || recommendation.plan?.key || ''),
      ) || recommendation.plan || GENERATED_PLAN_CATALOG[0],
    [selectedPlanKey, recommendation.plan],
  );

  const headroomLabel = useMemo(() => {
    if (!selectedPlan) return null;
    if (selectedPlan.contact_only) return null;
    if (selectedPlan.points_per_period === UNLIMITED_BUDGET) {
      return 'Unlimited (metered) — pay only for what you run';
    }
    const turnsAtTier = turnsCoveredAt(selectedPlan.key, tier);
    if (turnsAtTier == null) return null;
    return `~${formatNumber(turnsAtTier)} ${TIERS.find((t) => t.key === tier).sub} turns / month at this plan`;
  }, [selectedPlan, tier]);

  // The truth line: how the user's estimate stacks up against the
  // currently-selected plan (which may differ from the recommendation
  // if they overrode it). One source of the page's honesty.
  const fitMessage = useMemo(() => {
    if (!selectedPlan || estimatedPoints <= 0) return null;
    if (selectedPlan.contact_only) {
      return 'Enterprise plans are arranged with our team — usage caps are negotiated per contract.';
    }
    if (selectedPlan.points_per_period === UNLIMITED_BUDGET) {
      const monthly = (estimatedPoints / 1000).toFixed(0);
      return `At your estimate, Pay-as-you-go would bill ~$${monthly} this month (${formatNumber(estimatedPoints)} pts × $0.001).`;
    }
    const budget = selectedPlan.points_per_period;
    if (estimatedPoints <= budget * 0.8) {
      return `${selectedPlan.name} covers your estimate with ${Math.round((1 - estimatedPoints / budget) * 100)}% headroom.`;
    }
    if (estimatedPoints <= budget) {
      return `${selectedPlan.name} covers your estimate, but you'll be near the cap. Consider the next tier for headroom.`;
    }
    const overBy = estimatedPoints - budget;
    return `Your estimate exceeds ${selectedPlan.name}'s ${formatNumber(budget)}-pt cap by ${formatNumber(overBy)} pts. A higher tier or PayG top-ups would prevent mid-month run blocks.`;
  }, [selectedPlan, estimatedPoints]);

  const handleChoose = useCallback(async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!selectedPlan) return;
    if (selectedPlan.contact_only) {
      window.location.href = `mailto:sales@autosynth.io?subject=StepFlow%20${encodeURIComponent(selectedPlan.name)}%20plan`;
      return;
    }
    setCheckoutLoading(true);
    try {
      const url = await createSubscriptionCheckout(selectedPlan.key, {
        successPath: '/pricing',
        cancelPath: '/pricing',
      });
      window.location.href = url;
    } catch (e) {
      showToast(e.message || 'Checkout failed', 'error');
    } finally {
      setCheckoutLoading(false);
    }
  }, [user, selectedPlan, navigate, showToast]);

  // Open the Stripe Billing Portal — the canonical surface for plan
  // upgrade/downgrade, payment method, invoices, and cancellation.
  // Same-tab redirect here (unlike the workbench button which opens a
  // new tab) because /pricing is a dedicated billing route, not an
  // in-context workbench surface — losing it on redirect is fine.
  const handleManage = useCallback(async () => {
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      const url = await openBillingPortal({ returnPath: '/pricing' });
      window.location.href = url;
    } catch (e) {
      showToast(e?.message || 'Could not open billing portal — try again.', 'error');
      setPortalLoading(false);
    }
  }, [portalLoading, showToast]);

  const billingRequired = searchParams.get('billing') === 'required';
  const dismissBillingBanner = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('billing');
    setSearchParams(next, { replace: true });
  };
  const hasActive = Boolean(status?.stripeSubscriptionId);
  // Resolve the active plan's catalog row so the hero can show its
  // price + monthly points budget instead of just echoing the key.
  const currentPlan = useMemo(
    () =>
      hasActive && status?.planKey
        ? GENERATED_PLAN_CATALOG.find(
            (p) => p.key === String(status.planKey).toLowerCase(),
          )
        : null,
    [hasActive, status?.planKey],
  );

  const pickPlan = (planKey) => {
    setSelectedPlanKey(planKey);
    setManualOverride(true);
    setDropdownOpen(false);
  };

  const Icon = planIcon(selectedPlan?.key || '');

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 pb-16 sm:px-6 lg:px-8">
        {/* ──────────────────────────────────────────────────────────────
            Hero — context-aware. Active subscribers see a live plan
            status card; prospects / billing-required users see the
            acquisition headline. No product name (they're already in it).
        ────────────────────────────────────────────────────────────── */}
        {hasActive ? (
          <motion.div
            variants={fadeUp}
            initial="initial"
            animate="animate"
            transition={{ duration: 0.4, ease }}
            className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50 p-6 shadow-sm sm:p-7"
          >
            {/* Soft animated glow accent — slow drift, purely decorative. */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-400/10 blur-3xl"
              animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="relative flex flex-wrap items-center gap-4">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 260, damping: 18 }}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600/10 text-blue-600"
              >
                {React.createElement(planIcon(currentPlan?.key || status.planKey || ''), {
                  className: 'h-6 w-6',
                  'aria-hidden': true,
                })}
              </motion.div>
              <div className="min-w-0 flex-1">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200/60">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  Active
                </span>
                <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900">
                  You're on{' '}
                  <span className="capitalize">{currentPlan?.name || status.planKey || 'your plan'}</span>
                </h1>
                <p className="mt-0.5 text-sm text-slate-500">
                  Upgrade, downgrade, update payment, or cancel — all in one place.
                </p>
              </div>
              {currentPlan ? (
                <div className="flex shrink-0 items-center gap-2">
                  {formatPriceCents(currentPlan.price_cents) ? (
                    <span className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 ring-1 ring-slate-200">
                      {formatPriceCents(currentPlan.price_cents)}
                      <span className="text-xs font-normal text-slate-400">/{currentPlan.interval || 'mo'}</span>
                    </span>
                  ) : null}
                  {currentPlan.points_per_period > 0 ? (
                    <span className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 ring-1 ring-slate-200 tabular-nums">
                      {formatNumber(currentPlan.points_per_period)}
                      <span className="text-xs font-normal text-slate-400"> pts/mo</span>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : (
          <motion.div
            variants={fadeUp}
            initial="initial"
            animate="animate"
            transition={{ duration: 0.4, ease }}
            className="text-center"
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600">Plans &amp; usage</p>
            <h1 className="mx-auto mt-3 max-w-2xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {billingRequired ? 'Pick a plan to unlock the workbench' : 'How much will you use?'}
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
              Your bill scales with usage, not seats. Estimate below — we'll point you to the plan that fits.
            </p>
            {billingRequired ? (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.3 }}
                className="mx-auto mt-6 inline-flex max-w-2xl items-start gap-2 rounded-full bg-amber-50/90 px-4 py-2 text-left text-sm text-amber-900 ring-1 ring-amber-200/60"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                <span>
                  Complete checkout to start runs in workspaces you own.
                  <button
                    type="button"
                    onClick={dismissBillingBanner}
                    className="ml-2 inline-flex items-center rounded p-0.5 text-amber-700 hover:text-amber-900"
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              </motion.div>
            ) : null}
          </motion.div>
        )}

        {loading ? (
          <div className="mt-12 flex justify-center text-slate-500">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
        ) : (
          <>
            {/* ──────────────────────────────────────────────────────────
                Calculator
            ────────────────────────────────────────────────────────── */}
            <motion.div
              variants={fadeUp}
              initial="initial"
              animate="animate"
              transition={{ duration: 0.4, ease, delay: 0.08 }}
              className="mt-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {hasActive ? 'Explore tiers' : 'Estimate your usage'}
              </p>

              <div className="mt-4">
                <label htmlFor="turns-slider" className="flex items-baseline justify-between text-sm">
                  <span className="font-medium text-slate-900">Agent turns per month</span>
                  <span className="tabular-nums text-slate-700">
                    <span className="text-lg font-semibold">{formatNumber(turns)}</span>
                    <span className="ml-1 text-slate-500">turns</span>
                  </span>
                </label>
                <input
                  id="turns-slider"
                  type="range"
                  min={SLIDER_MIN}
                  max={SLIDER_MAX}
                  step={SLIDER_STEP}
                  value={turns}
                  onChange={(e) => setTurns(Number(e.target.value))}
                  className="mt-3 w-full accent-blue-600"
                />
                <div className="mt-1 flex justify-between text-[11px] text-slate-400 tabular-nums">
                  <span>{SLIDER_MIN}</span>
                  <span>{formatNumber(SLIDER_MAX)}+</span>
                </div>
              </div>

              <div className="mt-5">
                <p className="text-sm font-medium text-slate-900">Mostly using</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {TIERS.map((t) => {
                    const active = t.key === tier;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTier(t.key)}
                        className={[
                          'rounded-lg border px-3 py-2 text-left transition',
                          active
                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500/40'
                            : 'border-slate-200 bg-white hover:bg-slate-50',
                        ].join(' ')}
                        aria-pressed={active}
                      >
                        <p className={`text-sm font-semibold ${active ? 'text-blue-900' : 'text-slate-900'}`}>
                          {t.label}
                          <span className={`ml-1 text-xs font-normal ${active ? 'text-blue-700/80' : 'text-slate-500'}`}>
                            {t.sub}
                          </span>
                        </p>
                        <p className={`mt-0.5 text-[11px] leading-snug ${active ? 'text-blue-800/70' : 'text-slate-500'}`}>
                          {t.hint}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Result line */}
              <div className="mt-6 rounded-lg border border-slate-200/80 bg-slate-50/70 px-4 py-3 text-sm">
                <p className="text-slate-700">
                  Estimated{' '}
                  <span className="font-semibold tabular-nums text-slate-900">
                    {formatNumber(estimatedPoints)} pts / month
                  </span>
                  {recommendation.plan && !manualOverride ? (
                    <>
                      {' '}
                      → <span className="font-medium text-blue-700">{recommendation.plan.name}</span>
                      {recommendation.plan.price_cents != null
                        ? ` (${formatPriceCents(recommendation.plan.price_cents)}/mo)`
                        : ''}
                    </>
                  ) : null}
                  {manualOverride ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => setManualOverride(false)}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        (use recommendation)
                      </button>
                    </>
                  ) : null}
                </p>
                {recommendation.paygCheaper && !recommendation.overflowsAllFinite ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Pay-as-you-go would be cheaper at this scale — pick PayG below if you prefer metered billing.
                  </p>
                ) : null}
                {recommendation.overflowsAllFinite ? (
                  <p className="mt-1 text-xs text-amber-800">
                    Heavier than our top finite plan. Use Pay-as-you-go or talk to us about Enterprise.
                  </p>
                ) : null}
              </div>
            </motion.div>

            {/* ──────────────────────────────────────────────────────────
                Plan dropdown + CTA
            ────────────────────────────────────────────────────────── */}
            <motion.div
              variants={fadeUp}
              initial="initial"
              animate="animate"
              transition={{ duration: 0.4, ease, delay: 0.16 }}
              className="mt-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {hasActive ? 'Compare plans' : 'Your plan'}
              </p>
              {hasActive ? (
                <p className="mt-1 text-xs text-slate-500">
                  Browse the tiers, then use “Manage subscription” to switch — changes are prorated automatically.
                </p>
              ) : null}

              <button
                ref={dropdownAnchorRef}
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                className="mt-3 flex w-full items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 text-left transition hover:border-slate-400"
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <Icon className="h-5 w-5 shrink-0 text-blue-500" aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">
                      {selectedPlan?.name}
                      {selectedPlan?.badge ? (
                        <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                          {selectedPlan.badge}
                        </span>
                      ) : null}
                      {!manualOverride && recommendation.plan?.key === selectedPlan?.key ? (
                        <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                          Recommended
                        </span>
                      ) : null}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {planRowSummary(selectedPlan)}
                    </span>
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
              </button>

              <PortalMenu
                anchorRef={dropdownAnchorRef}
                open={dropdownOpen}
                onClose={() => setDropdownOpen(false)}
                align="bottom-right"
                offset={6}
                className="rounded-xl border border-slate-200 bg-white shadow-xl"
                width={dropdownAnchorRef.current?.offsetWidth}
              >
                <ul role="listbox" className="max-h-72 overflow-y-auto py-1">
                  {GENERATED_PLAN_CATALOG.map((plan) => {
                    const isSelected = plan.key === selectedPlan?.key;
                    const isRecommended = plan.key === recommendation.plan?.key;
                    return (
                      <li key={plan.key}>
                        {/* role="option" goes on the clickable element so
                            ``getByRole('option')`` returns the button and
                            ``fireEvent.click`` fires the handler. Putting
                            it on a wrapper <li> made the test target an
                            element whose click never reached onClick. */}
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => pickPlan(plan.key)}
                          className={[
                            'flex w-full items-start gap-3 px-4 py-3 text-left transition',
                            isSelected ? 'bg-blue-50' : 'hover:bg-slate-50',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-300',
                            ].join(' ')}
                            aria-hidden
                          >
                            {isSelected ? <Check className="h-3 w-3 text-white" /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-slate-900">{plan.name}</span>
                              {isRecommended && !isSelected ? (
                                <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                                  Recommended
                                </span>
                              ) : null}
                            </span>
                            <span className="block text-xs text-slate-500">{planRowSummary(plan)}</span>
                            {plan.description ? (
                              <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                                {plan.description}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </PortalMenu>

              {headroomLabel ? (
                <p className="mt-3 text-xs text-slate-500">{headroomLabel}</p>
              ) : null}

              {fitMessage ? (
                <p
                  className={[
                    'mt-3 rounded-lg px-3 py-2 text-xs',
                    estimatedPoints > (selectedPlan?.points_per_period || 0)
                    && selectedPlan?.points_per_period > 0
                      ? 'bg-amber-50 text-amber-900'
                      : 'bg-slate-50 text-slate-700',
                  ].join(' ')}
                >
                  {fitMessage}
                </p>
              ) : null}

              {hasActive ? (
                /* Active subscriber: plan changes (upgrade / downgrade /
                   cancel / payment method) all go through the Stripe
                   Billing Portal. We do NOT route them back through
                   Checkout — that would create a parallel subscription.
                   The dropdown above stays as a "compare plans"
                   reference; the Portal is where switching happens. */
                <>
                  <button
                    type="button"
                    onClick={handleManage}
                    disabled={portalLoading}
                    className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                  >
                    {portalLoading ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Opening billing portal…
                      </span>
                    ) : (
                      'Manage subscription — change plan, payment, or cancel'
                    )}
                  </button>
                  <p className="mt-3 text-center text-[11px] text-slate-500">
                    Upgrades take effect immediately (prorated); downgrades and
                    cancellations apply at the end of your billing period.
                    Managed securely in the Stripe customer portal.
                  </p>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleChoose}
                    disabled={checkoutLoading}
                    className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                  >
                    {checkoutLoading ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
                      </span>
                    ) : selectedPlan?.contact_only ? (
                      'Contact sales'
                    ) : selectedPlan?.points_per_period === UNLIMITED_BUDGET ? (
                      'Continue with Pay-as-you-go'
                    ) : (
                      `Subscribe to ${selectedPlan?.name}${
                        selectedPlan?.price_cents != null
                          ? ` — ${formatPriceCents(selectedPlan.price_cents)}/mo`
                          : ''
                      }`
                    )}
                  </button>
                  <p className="mt-3 text-center text-[11px] text-slate-500">
                    Stripe-hosted checkout. Card details never touch our servers. Cancel anytime.
                  </p>
                </>
              )}
            </motion.div>
          </>
        )}

        <p className="mt-10 text-center text-[11px] text-slate-400">
          Per-turn estimate assumes ~1.5k input + 0.5k output tokens. Actual
          usage is metered per token at the model's published rate.
        </p>
      </div>
    </div>
  );
}
